import { useTranslations } from "next-intl";
import { z } from "zod";
import {
	type CountryCode,
	isValidEmail,
	isValidNationalId,
	validateBankAccount,
	validateEmail,
} from "@leabankab/component-library";
import { useCountrySettings } from "@/hooks/use-app-country";
import type { Applicant } from "@/types/form";
import {
	mainApplicantContactRef,
	mainApplicantNationalIdRef,
} from "@/utils/main-applicant-national-id-ref";
import { areDuplicateEmails } from "@/utils/validation/duplicate-co-applicant-email";
import { areDuplicateNationalIds } from "@/utils/validation/duplicate-co-applicant-national-id";
import {
	type NationalIdCountry,
	getNationalIdLength,
	normalizeNationalId,
} from "@/utils/validation/normalize-national-id";
import { validateMobileNumber } from "@/utils/validation/validate-mobile-number";

/** Country whose phone and national ID rules the forms apply. */
export const useValidationCountry = (): NationalIdCountry =>
	useCountrySettings().validationCountry;

export type ContactApplicantFields = Pick<
	Applicant,
	| "firstName"
	| "lastName"
	| "nationalId"
	| "mobileCountry"
	| "mobileNumber"
	| "emailAddress"
>;

export const useNationalIdSchema = () => {
	const t = useTranslations("forms.errors");
	const country = useValidationCountry();

	return z
		.string()
		.trim()
		.min(1, { message: t("general.required") })
		.superRefine((value, ctx) => {
			const normalized = normalizeNationalId(country, value);

			if (normalized.length !== getNationalIdLength(country)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t(
						"personalInformationForm.invalidNationalNumberLength"
					),
				});
				return;
			}

			if (!isValidNationalId(country, normalized)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("personalInformationForm.invalidNationalNumber"),
				});
			}
		})
		.transform((value) => normalizeNationalId(country, value));
};

export const useEmailAddressSchema = () => {
	const t = useTranslations("forms.errors");

	return z
		.string()
		.trim()
		.min(1, { message: t("general.required") })
		.superRefine((value, ctx) => {
			if (!isValidEmail(value)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("personalInformationForm.invalidEmail"),
				});
			}
		})
		.transform((value) => validateEmail(value).normalized!);
};

export const useBankAccountSchema = () => {
	const t = useTranslations("forms.errors");
	const country = useValidationCountry();

	return z
		.string()
		.trim()
		.min(1, { message: t("general.required") })
		.superRefine((value, ctx) => {
			const result = validateBankAccount(country, value);

			if (!result.valid || !result.normalized) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("personalInformationForm.invalidBankAccount"),
				});
			}
		})
		.transform((value) => validateBankAccount(country, value).normalized!);
};

export const useContactApplicantSchema = () => {
	const t = useTranslations("forms.errors");
	const nationalId = useNationalIdSchema();
	const emailAddress = useEmailAddressSchema();
	const defaultPhoneCountry = useValidationCountry();

	return z
		.object({
			firstName: z.string().optional(),
			lastName: z.string().optional(),
			nationalId,
			mobileCountry: z.string().min(2).default(defaultPhoneCountry),
			mobileNumber: z
				.string()
				.trim()
				.min(1, { message: t("general.required") }),
			emailAddress,
		})
		.superRefine((data, ctx) => {
			const phoneCountry = (data.mobileCountry ||
				defaultPhoneCountry) as CountryCode;
			const result = validateMobileNumber(
				phoneCountry,
				data.mobileNumber
			);

			if (!result.valid || !result.normalized) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("personalInformationForm.invalidPhoneNumber"),
					path: ["mobileNumber"],
				});
			}
		}) satisfies z.ZodType<ContactApplicantFields>;
};

export const useCoApplicantContactSchema = (
	mainApplicant?: Pick<
		Applicant,
		"nationalId" | "mobileCountry" | "mobileNumber" | "emailAddress"
	>
) => {
	const contactSchema = useContactApplicantSchema();
	const t = useTranslations("forms.errors");
	const country = useValidationCountry();

	return contactSchema.superRefine((data, ctx) => {
		const mainContact = mainApplicant ?? mainApplicantContactRef.current;
		const resolvedMainNationalId =
			mainContact?.nationalId ?? mainApplicantNationalIdRef.current;

		if (
			areDuplicateNationalIds(
				country,
				resolvedMainNationalId,
				data.nationalId
			)
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: t(
					"personalInformationForm.duplicateCoApplicantNationalId"
				),
				path: ["nationalId"],
			});
		}

		if (areDuplicateEmails(mainContact?.emailAddress, data.emailAddress)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: t("personalInformationForm.duplicateCoApplicantEmail"),
				path: ["emailAddress"],
			});
		}

		const mainPhone = mainContact?.mobileNumber
			? validateMobileNumber(
					(mainContact.mobileCountry || country) as CountryCode,
					mainContact.mobileNumber
				).normalized
			: undefined;
		const coApplicantPhone = validateMobileNumber(
			(data.mobileCountry || country) as CountryCode,
			data.mobileNumber
		).normalized;
		if (mainPhone && coApplicantPhone && mainPhone === coApplicantPhone) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: t("personalInformationForm.duplicateCoApplicantPhone"),
				path: ["mobileNumber"],
			});
		}
	});
};
